import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import './RiskAlertsPopup.css';

const RiskAlertsPopup = ({ 
    isOpen, 
    onClose, 
    partnerName, 
    partnerId,
    redFlags, 
    legalDisputes,
    sanctionScreening 
}) => {
    const history = useHistory();

    if (!isOpen) return null;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Unknown date';
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleRedFlagsClick = () => {
        onClose();
        history.push(`/dashboard/crm/profile/${partnerId}?tab=ai-notes`);
    };

    const handleLegalDisputesClick = () => {
        onClose();
        history.push(`/dashboard/crm/profile/${partnerId}?tab=external-data`);
    };

    const handleSanctionScreeningClick = () => {
        onClose();
        history.push(`/dashboard/crm/profile/${partnerId}?tab=kyc-compliance`);
    };

    return (
        <div className="risk-alerts-overlay">
            <div className="risk-alerts-popup">
                {/* Header */}
                <div className="risk-alerts-header">
                    <div className="risk-alerts-title">
                        <FontAwesomeIcon 
                            icon={faExclamationTriangle} 
                            className="risk-alerts-icon" 
                        />
                        <h3>Risk Alerts</h3>
                    </div>
                    <button 
                        className="risk-alerts-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Partner Info */}
                <div className="risk-alerts-partner">
                    <span className="risk-alerts-partner-label">Partner:</span>
                    <span className="risk-alerts-partner-name">{partnerName}</span>
                </div>

                {/* Content */}
                <div className="risk-alerts-content">
                    {/* Red Flags Section
                        Checked through to `flags` rather than on `redFlags`
                        alone: the field arrives as an empty ARRAY when there is
                        nothing to report, and `[]` is truthy — which rendered
                        the section and threw on `undefined.map`, taking the
                        whole dashboard down with it. */}
                    {redFlags?.flags?.length > 0 && (
                        <div className="risk-alerts-section">
                            <div className="risk-alerts-section-header">
                                <h4>Red Flags</h4>
                                <span className="risk-alerts-timestamp">
                                    From analysis on {formatTimestamp(redFlags.timestamp)}
                                </span>
                            </div>
                            <div className="risk-alerts-red-flags">
                                <ul>
                                    {redFlags.flags.map((flag, index) => (
                                        <li key={index}>{flag}</li>
                                    ))}
                                </ul>
                            </div>
                            <button 
                                className="risk-alerts-nav-btn red-flags-btn"
                                onClick={handleRedFlagsClick}
                            >
                                View AI Notes Analysis →
                            </button>
                        </div>
                    )}

                    {/* Legal Disputes Section */}
                    {legalDisputes && (
                        <div className="risk-alerts-section">
                            <div className="risk-alerts-section-header">
                                <h4>Legal Disputes</h4>
                                <span className="risk-alerts-timestamp">
                                    From analysis on {formatTimestamp(legalDisputes.timestamp)}
                                </span>
                            </div>
                            <div className="risk-alerts-legal-disputes">
                                <p>{legalDisputes.disputes}</p>
                            </div>
                            <button 
                                className="risk-alerts-nav-btn legal-disputes-btn"
                                onClick={handleLegalDisputesClick}
                            >
                                View External Data Analysis →
                            </button>
                        </div>
                    )}

                    {/* Sanction Screening Section */}
                    {sanctionScreening && sanctionScreening.suspect_flag && (
                        <div className="risk-alerts-section sanction-section">
                            <div className="risk-alerts-section-header">
                                <h4>Sanctions Screening Match</h4>
                                <span className="risk-alerts-timestamp">
                                    Screened on {formatTimestamp(sanctionScreening.screening_date)}
                                </span>
                            </div>
                            <div className="risk-alerts-sanction-screening">
                                <div className="sanction-warning">
                                    <FontAwesomeIcon 
                                        icon={faExclamationTriangle} 
                                        className="sanction-warning-icon" 
                                    />
                                    <span className="sanction-warning-text">
                                        This entity has been flagged in sanctions screening
                                    </span>
                                </div>
                                <div className="sanction-details">
                                    <div className="sanction-detail-item">
                                        <span className="sanction-label">Match Score:</span>
                                        <span className="sanction-value">{sanctionScreening.highest_score}%</span>
                                    </div>
                                    <div className="sanction-detail-item">
                                        <span className="sanction-label">Sensitivity Threshold:</span>
                                        <span className="sanction-value">{sanctionScreening.sensitivity}%</span>
                                    </div>
                                    {sanctionScreening.matched_lists && sanctionScreening.matched_lists.length > 0 && (
                                        <div className="sanction-detail-item">
                                            <span className="sanction-label">Matched Lists:</span>
                                            <div className="sanction-lists">
                                                {sanctionScreening.matched_lists.map((list, index) => (
                                                    <div key={index} className="sanction-list-item">
                                                        <strong>{list.list_name}</strong> ({list.list_org})
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button 
                                className="risk-alerts-nav-btn sanction-btn"
                                onClick={handleSanctionScreeningClick}
                            >
                                View KYC Compliance Details →
                            </button>
                        </div>
                    )}

                    {/* No Alerts Message (fallback) */}
                    {!redFlags && !legalDisputes && !sanctionScreening && (
                        <div className="risk-alerts-no-data">
                            <p>No risk alerts found for this partner.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="risk-alerts-footer">
                    <button 
                        className="risk-alerts-close-footer-btn"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RiskAlertsPopup; 